import React from "react";
import Box from "@mui/material/Box";
import { SITE_BG } from "../../../dark-theme/palette";

const BUBBLES = [
    { left: "4%", size: 36, delay: "0s", duration: "11s", sway: "18px", bottom: "1%" },
    { left: "14%", size: 18, delay: "1.1s", duration: "9s", sway: "-14px", bottom: "0%" },
    { left: "24%", size: 48, delay: "0.4s", duration: "14s", sway: "22px", bottom: "5%" },
    { left: "36%", size: 22, delay: "2s", duration: "10s", sway: "-16px", bottom: "2%" },
    { left: "46%", size: 30, delay: "0.2s", duration: "12s", sway: "14px", bottom: "0%" },
    { left: "56%", size: 54, delay: "1.6s", duration: "15s", sway: "-24px", bottom: "7%" },
    { left: "66%", size: 20, delay: "2.6s", duration: "9s", sway: "12px", bottom: "3%" },
    { left: "76%", size: 38, delay: "0.8s", duration: "13s", sway: "-18px", bottom: "1%" },
    { left: "86%", size: 16, delay: "3s", duration: "8s", sway: "10px", bottom: "4%" },
    { left: "92%", size: 28, delay: "1.3s", duration: "11s", sway: "-12px", bottom: "6%" },
    { left: "10%", size: 24, delay: "3.6s", duration: "12s", sway: "15px", bottom: "8%" },
    { left: "42%", size: 42, delay: "2.2s", duration: "16s", sway: "-20px", bottom: "0%" },
    { left: "70%", size: 19, delay: "4.1s", duration: "10s", sway: "11px", bottom: "5%" },
    { left: "30%", size: 14, delay: "0.7s", duration: "7.5s", sway: "-8px", bottom: "2%" },
    { left: "80%", size: 32, delay: "2.9s", duration: "13.5s", sway: "17px", bottom: "9%" },
];

const MOTES = [
    { left: "8%", top: "22%", size: 3, delay: "0s", duration: "9s", dx: "40px", dy: "-60px" },
    { left: "22%", top: "68%", size: 2, delay: "1.2s", duration: "11s", dx: "-30px", dy: "-80px" },
    { left: "55%", top: "18%", size: 4, delay: "0.5s", duration: "8s", dx: "50px", dy: "30px" },
    { left: "78%", top: "42%", size: 2, delay: "2s", duration: "10s", dx: "-45px", dy: "-40px" },
    { left: "35%", top: "80%", size: 3, delay: "1.6s", duration: "12s", dx: "35px", dy: "-70px" },
    { left: "88%", top: "75%", size: 2, delay: "0.9s", duration: "9.5s", dx: "-25px", dy: "-55px" },
    { left: "15%", top: "40%", size: 3, delay: "2.8s", duration: "11s", dx: "28px", dy: "45px" },
    { left: "62%", top: "55%", size: 2, delay: "3.4s", duration: "8.5s", dx: "-38px", dy: "-35px" },
];

const DEFAULT_THEME = {
    base: "#c5d5ce",
    mistOrb: "rgba(180,220,200,0.35)",
    mistOrbSoft: "rgba(200,230,215,0.14)",
    shaft: "rgba(220,245,230,0.22)",
};

/** Night wash for play/select surfaces: same fog and drift, read as moonlit instead of daylight. */
const NIGHT_THEME = {
    base: SITE_BG,
    mistOrb: "rgba(96,150,190,0.30)",
    mistOrbSoft: "rgba(70,110,160,0.16)",
    shaft: "rgba(150,200,235,0.16)",
};

/**
 * Living ambient backdrop for puzzle play/select — mist photo + light shafts and motes.
 * Rising bubbles are off by default (`showBubbles`).
 * pointer-events: none; transform/opacity only.
 * Optional `theme` / `baseColor` tints the wash (e.g. weekly homepage sets).
 * `variant="night"` keeps the same motion on the site's dark slate.
 */
export default function SlidingPlayMistBackground({
    theme = null,
    baseColor = null,
    variant = "mist",
    showBubbles = false,
} = {}) {
    const night = variant === "night" && !theme && !baseColor;
    const fallback = night ? NIGHT_THEME : DEFAULT_THEME;
    const base = baseColor || theme?.base || fallback.base;
    const mistOrb = theme?.mistOrb || fallback.mistOrb;
    const mistOrbSoft = theme?.mistOrbSoft || fallback.mistOrbSoft;
    const shaft = theme?.shaft || fallback.shaft;
    /** The mist photo is daylight mint: dim and cool it so it fogs the dark slate. */
    const mistImageSx = night
        ? { opacity: 0.34, filter: "brightness(0.42) saturate(0.72) hue-rotate(184deg)" }
        : null;
    const glow = night ? 0.55 : 1;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                overflow: "hidden",
                pointerEvents: "none",
                "@keyframes slidingBgShift": {
                    "0%": { backgroundPosition: "0% 40%" },
                    "50%": { backgroundPosition: "100% 60%" },
                    "100%": { backgroundPosition: "0% 40%" },
                },
                "@keyframes slidingMistDriftA": {
                    "0%": { transform: "translate3d(-10%, 6%, 0) scale(1)" },
                    "50%": { transform: "translate3d(14%, -10%, 0) scale(1.12)" },
                    "100%": { transform: "translate3d(-10%, 6%, 0) scale(1)" },
                },
                "@keyframes slidingMistDriftB": {
                    "0%": { transform: "translate3d(14%, -4%, 0) scale(1.08)" },
                    "50%": { transform: "translate3d(-18%, 12%, 0) scale(1)" },
                    "100%": { transform: "translate3d(14%, -4%, 0) scale(1.08)" },
                },
                "@keyframes slidingMistDriftC": {
                    "0%": { transform: "translate3d(-6%, 12%, 0) scale(1.14)" },
                    "50%": { transform: "translate3d(10%, -12%, 0) scale(1.02)" },
                    "100%": { transform: "translate3d(-6%, 12%, 0) scale(1.14)" },
                },
                "@keyframes slidingLightShaft": {
                    "0%": { transform: "translate3d(-20%, 0, 0) rotate(18deg)", opacity: 0.12 },
                    "40%": { opacity: 0.28 },
                    "100%": { transform: "translate3d(40%, 0, 0) rotate(18deg)", opacity: 0.08 },
                },
                "@keyframes slidingLightShaftB": {
                    "0%": { transform: "translate3d(30%, 0, 0) rotate(-14deg)", opacity: 0.1 },
                    "50%": { opacity: 0.24 },
                    "100%": { transform: "translate3d(-35%, 0, 0) rotate(-14deg)", opacity: 0.08 },
                },
                "@keyframes slidingMistVeil": {
                    "0%, 100%": { opacity: 0.45 },
                    "50%": { opacity: 0.85 },
                },
                "@keyframes slidingBubbleRise": {
                    "0%": {
                        transform: "translate3d(0, 0, 0) scale(0.75)",
                        opacity: 0,
                    },
                    "6%": { opacity: 0.55 },
                    "45%": {
                        transform: "translate3d(var(--sway), -50vh, 0) scale(1.05)",
                        opacity: 0.48,
                    },
                    "100%": {
                        transform: "translate3d(calc(var(--sway) * -0.5), -118vh, 0) scale(0.85)",
                        opacity: 0,
                    },
                },
                "@keyframes slidingMoteDrift": {
                    "0%": {
                        transform: "translate3d(0, 0, 0)",
                        opacity: 0,
                    },
                    "15%": { opacity: 0.7 },
                    "85%": { opacity: 0.45 },
                    "100%": {
                        transform: "translate3d(var(--dx), var(--dy), 0)",
                        opacity: 0,
                    },
                },
                "@keyframes slidingVignettePulse": {
                    "0%, 100%": { opacity: 0.55 },
                    "50%": { opacity: 0.85 },
                },
            }}
        >
            <Box sx={{ position: "absolute", inset: 0, backgroundColor: base }} />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: "url(/bg_images/mint-mist-gradient-bg.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    // Slightly oversized so the slow position shift can drift without edges.
                    backgroundSize: "110% 110%",
                    animation: "slidingBgShift 22s ease-in-out infinite",
                    ...mistImageSx,
                }}
            />
            {theme ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: base,
                        opacity: 0.72,
                        mixBlendMode: "color",
                    }}
                />
            ) : null}

            <Box
                sx={{
                    position: "absolute",
                    width: "95%",
                    height: "80%",
                    left: "-20%",
                    top: "-5%",
                    borderRadius: "50%",
                    background:
                        "radial-gradient(ellipse at center, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.18) 40%, transparent 68%)",
                    filter: "blur(24px)",
                    opacity: glow,
                    willChange: "transform",
                    animation: "slidingMistDriftA 16s ease-in-out infinite",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    width: "100%",
                    height: "75%",
                    right: "-25%",
                    bottom: "-8%",
                    borderRadius: "50%",
                    background: `radial-gradient(ellipse at center, ${mistOrb} 0%, ${mistOrbSoft} 42%, transparent 70%)`,
                    filter: "blur(30px)",
                    willChange: "transform",
                    animation: "slidingMistDriftB 20s ease-in-out infinite",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    width: "75%",
                    height: "60%",
                    left: "12%",
                    top: "28%",
                    borderRadius: "50%",
                    background: `radial-gradient(ellipse at center, rgba(255,255,255,0.32) 0%, ${mistOrbSoft} 48%, transparent 72%)`,
                    filter: "blur(36px)",
                    opacity: glow,
                    willChange: "transform",
                    animation: "slidingMistDriftC 24s ease-in-out infinite",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    top: "-20%",
                    left: "10%",
                    width: "28%",
                    height: "140%",
                    background:
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 45%, transparent 100%)",
                    filter: "blur(18px)",
                    willChange: "transform, opacity",
                    animation: "slidingLightShaft 14s ease-in-out infinite",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: "-15%",
                    right: "5%",
                    width: "22%",
                    height: "130%",
                    background: `linear-gradient(90deg, transparent 0%, ${shaft} 50%, transparent 100%)`,
                    filter: "blur(20px)",
                    willChange: "transform, opacity",
                    animation: "slidingLightShaftB 18s ease-in-out infinite",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: night
                        ? "linear-gradient(180deg, rgba(190,220,255,0.10) 0%, transparent 32%, transparent 58%, rgba(4,10,24,0.55) 100%)"
                        : "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 30%, transparent 58%, rgba(120,150,140,0.18) 100%)",
                    animation: "slidingMistVeil 12s ease-in-out infinite",
                }}
            />

            {showBubbles
                ? BUBBLES.map((b, i) => (
                <Box
                    key={`bubble-${i}`}
                    sx={{
                        position: "absolute",
                        left: b.left,
                        bottom: b.bottom,
                        width: b.size,
                        height: b.size,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.45) 38%, rgba(255,255,255,0.15) 68%, transparent 100%)",
                        border: "1.5px solid rgba(255,255,255,0.55)",
                        boxShadow:
                            "inset 0 0 8px rgba(255,255,255,0.45), 0 0 12px rgba(255,255,255,0.2)",
                        willChange: "transform, opacity",
                        "--sway": b.sway,
                        animation: `slidingBubbleRise ${b.duration} linear ${b.delay} infinite`,
                    }}
                />
            ))
                : null}

            {MOTES.map((m, i) => (
                <Box
                    key={`mote-${i}`}
                    sx={{
                        position: "absolute",
                        left: m.left,
                        top: m.top,
                        width: m.size,
                        height: m.size,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.85)",
                        boxShadow: "0 0 6px rgba(255,255,255,0.7)",
                        willChange: "transform, opacity",
                        "--dx": m.dx,
                        "--dy": m.dy,
                        animation: `slidingMoteDrift ${m.duration} ease-in-out ${m.delay} infinite`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: night
                        ? "radial-gradient(ellipse at center, transparent 32%, rgba(2,6,18,0.55) 100%)"
                        : "radial-gradient(ellipse at center, transparent 35%, rgba(90,120,110,0.22) 100%)",
                    animation: "slidingVignettePulse 10s ease-in-out infinite",
                }}
            />
        </Box>
    );
}
